import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EvaluationLayout from '../../components/EvaluationLayout';
import { ArrowLeft, Save } from 'lucide-react';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'react-hot-toast';
import templateService from '../../services/templateService';

const TemplateCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'coding',
    visibility: 'private',
    template_data: {
      messages: [
        { role: 'system', content: '' },
        { role: 'user', content: '' }
      ],
      metadata: {},
      expected_output: '',
      evaluation_criteria: [],
      // Add fields for wizard compatibility
      prompt: '',
      role: 'user',
      test_cases: [
        { input: '', expected: '' }
      ]
    },
    tags: []
  });

  const categories = [
    { value: 'coding', label: 'Coding' },
    { value: 'text', label: 'Text' },
    { value: 'essay', label: 'Essay' },
    { value: 'novel', label: 'Novel' },
    { value: 'custom', label: 'Custom' }
  ];

  const visibilityOptions = [
    { value: 'private', label: 'Private (Only You)' },
    { value: 'organization', label: 'Organization' },
    { value: 'public', label: 'Public' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    if (!formData.template_data.messages[0].content.trim()) {
      toast.error('System message is required');
      return;
    }

    if (!formData.template_data.messages[1].content.trim()) {
      toast.error('User message template is required');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Creating template...', { id: 'create-template' });

      // Sync prompt with user message if prompt is empty
      const dataToSend = { ...formData };
      if (!dataToSend.template_data.prompt.trim()) {
        dataToSend.template_data.prompt = dataToSend.template_data.messages[1].content;
      }

      // Log the data being sent
      console.log('Sending template data:', JSON.stringify(dataToSend, null, 2));

      await templateService.createTemplate(dataToSend);

      toast.success('Template created successfully!', { id: 'create-template' });
      navigate(`/templates`);
    } catch (error) {
      console.error('Failed to create template:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.message || 'Failed to create template', { id: 'create-template' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMessageChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      template_data: {
        ...prev.template_data,
        messages: prev.template_data.messages.map((msg, i) =>
          i === index ? { ...msg, [field]: value } : msg
        )
      }
    }));
  };

  const handleTemplateDataChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      template_data: {
        ...prev.template_data,
        [field]: value
      }
    }));
  };

  return (
    <EvaluationLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/templates')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Create Template</h1>
              <p className="text-muted-foreground mt-1">
                Create a new evaluation template
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Template Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter template name"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Enter template description"
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground min-h-[100px]"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                  required
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Visibility */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Visibility <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.visibility}
                  onChange={(e) => handleChange('visibility', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                  required
                >
                  {visibilityOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* System Message */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  System Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.template_data.messages[0].content}
                  onChange={(e) => handleMessageChange(0, 'content', e.target.value)}
                  placeholder="Enter system instructions..."
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground min-h-[100px] font-mono text-sm"
                  required
                />
                <p className="text-sm text-muted-foreground mt-1">
                  System instructions that guide the AI's behavior
                </p>
              </div>

              {/* User Message Template */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  User Message Template <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.template_data.messages[1].content}
                  onChange={(e) => handleMessageChange(1, 'content', e.target.value)}
                  placeholder="Enter user message template..."
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground min-h-[150px] font-mono text-sm"
                  required
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Use variables like {'{variable_name}'} in your template
                </p>
              </div>

              {/* Expected Output */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Expected Output (Optional)
                </label>
                <textarea
                  value={formData.template_data.expected_output}
                  onChange={(e) => handleTemplateDataChange('expected_output', e.target.value)}
                  placeholder="Describe the expected output format..."
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground min-h-[80px]"
                />
              </div>

              {/* Prompt for Wizard */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Evaluation Prompt
                </label>
                <textarea
                  value={formData.template_data.prompt}
                  onChange={(e) => handleTemplateDataChange('prompt', e.target.value)}
                  placeholder="Enter the prompt that will be used in evaluations..."
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground min-h-[100px]"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  This prompt will be prefilled when using this template in the evaluation wizard
                </p>
              </div>

              {/* Test Cases */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Test Cases
                </label>
                <div className="space-y-4">
                  {formData.template_data.test_cases.map((testCase, index) => (
                    <div key={index} className="border border-input rounded-md p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Test Case {index + 1}</span>
                        {formData.template_data.test_cases.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                template_data: {
                                  ...prev.template_data,
                                  test_cases: prev.template_data.test_cases.filter((_, i) => i !== index)
                                }
                              }));
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Input</label>
                        <textarea
                          value={testCase.input}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              template_data: {
                                ...prev.template_data,
                                test_cases: prev.template_data.test_cases.map((tc, i) =>
                                  i === index ? { ...tc, input: e.target.value } : tc
                                )
                              }
                            }));
                          }}
                          placeholder="Enter test input..."
                          className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground min-h-[60px] text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Expected Output</label>
                        <textarea
                          value={testCase.expected}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              template_data: {
                                ...prev.template_data,
                                test_cases: prev.template_data.test_cases.map((tc, i) =>
                                  i === index ? { ...tc, expected: e.target.value } : tc
                                )
                              }
                            }));
                          }}
                          placeholder="Enter expected output..."
                          className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground min-h-[60px] text-sm"
                        />
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        template_data: {
                          ...prev.template_data,
                          test_cases: [...prev.template_data.test_cases, { input: '', expected: '' }]
                        }
                      }));
                    }}
                  >
                    Add Test Case
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Test cases will be prefilled when using this template in the evaluation wizard
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/templates')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Creating...' : 'Create Template'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </EvaluationLayout>
  );
};

export default TemplateCreate;
